import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GraduationCap, HeartHandshake, Users, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { Formline } from "./Formline";

const OPPORTUNITY_BG = "/images/opportunities-bg.png";

const PILLARS = [
  {
    icon: GraduationCap,
    title: "Hands-On Training",
    body: "Industry-recognized security certification, first-aid readiness, de-escalation, and site-specific safety — provided so every recruit can step confidently into the field.",
  },
  {
    icon: Users,
    title: "Mentorship & Growth",
    body: "New team members are guided by experienced leaders who know the work and the communities. Careers are built here, not just shifts.",
  },
  {
    icon: HeartHandshake,
    title: "Community Impact",
    body: "Every hire strengthens Indigenous families and local economies. We invest in people first — the reputation of our work follows naturally.",
  },
];

export const Opportunities = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);

  return (
    <section
      id="opportunities"
      ref={ref}
      data-testid="opportunities-section"
      className="relative py-28 md:py-40 overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={OPPORTUNITY_BG}
          alt="Indigenous team gathered around a mentor during a security training session"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Overlays for legibility */}
      <div className="absolute inset-0 z-[1] bg-[#050505]/78" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#050505] via-[#050505]/70 to-[#050505]/30" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />

      <Formline
        className="absolute -right-16 top-10 w-[420px] h-[420px] rotate-6"
        opacity={0.06}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={16} className="text-[color:var(--primary-text)]" />
              <span className="text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--primary-text)]">
                Creating Opportunities
              </span>
              <span className="h-px w-16 bg-white/20" />
            </div>

            <h2
              data-testid="opportunities-heading"
              className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]"
            >
              Careers built for{" "}
              <span className="font-editorial italic text-[color:var(--primary-text)]">
                Indigenous people
              </span>
              .
            </h2>

            <p
              data-testid="opportunities-intro"
              className="mt-8 text-lg md:text-xl text-secondary leading-relaxed max-w-xl"
            >
              At Chowat Security Services Inc., we don&apos;t just hire — we
              invest in our people. We create real pathways to meaningful
              employment for Indigenous men and women, combining professional
              training with mentorship, cultural respect, and long-term career
              development in the security industry.
            </p>

            <p className="mt-6 text-lg md:text-xl text-secondary leading-relaxed max-w-xl">
              From first certification to leadership on major sites, our team
              grows together — carrying skills, pride, and opportunity back into
              their families and communities.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="opportunities-cta"
                className="group inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-semibold px-7 py-4 rounded-sm transition-colors"
              >
                Join Our Team
                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </a>
              <span className="text-xs uppercase tracking-[0.24em] text-white/60">
                Training · Mentorship · Growth
              </span>
            </div>
          </Reveal>

          <div className="lg:col-span-6 lg:pl-8 space-y-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`opportunity-pillar-${i + 1}`}
                  className="group relative flex items-start gap-5 rounded-sm border border-white/10 bg-[#0a0a0a]/70 backdrop-blur-md p-6 md:p-7 hover:border-[color:var(--primary)]/60 transition-colors"
                >
                  <div className="shrink-0 grid place-items-center h-12 w-12 rounded-sm bg-[color:var(--primary)]/15 text-[color:var(--primary-text)] border border-[color:var(--primary)]/30">
                    <p.icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-secondary leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
